import type {
    DeepPartial,
    MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapRuleWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useState } from 'react';
import {
    BodyMediumRegular,
    H6Regular,
    IconButton,
    LabelRegular,
    PercentInput,
    RowList,
    SmoothieScrollView,
    SpinnerIcon,
    StyledBodyMediumRegularInputNoOutline,
    Trash2Icon,
    useToastController,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { MutationSuccessToastOptions } from '../../../common/components/Toasts';

const parsePercentString = (s: string): number => {
    const n = parseInt(s.replace(/\s*%\s*/g, ''), 10);
    return Number.isFinite(n) ? n / 100 : 0;
};

const percentToInputString = (decimal: number): string => {
    const whole = Math.round((decimal || 0) * 100);
    return whole > 0 ? `${whole} %` : '';
};

interface PercentageSplitRowProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
}

const PercentageSplitRow = ({
    label,
    value,
    onChange,
}: PercentageSplitRowProps) => (
    <XStack
        backgroundColor="$gray100"
        paddingHorizontal={16}
        paddingVertical={10}
        alignItems="center"
        gap={12}
    >
        <BodyMediumRegular flex={1} numberOfLines={1}>
            {label}
        </BodyMediumRegular>
        <PercentInput
            value={value}
            onChangeText={onChange}
            size="tiny"
            width={88}
            textAlign="right"
            fontWeight="regular"
        />
    </XStack>
);

export interface GoalGroupEditorPanelProps {
    group: DeepPartial<MoneyMapRuleGroupType>;
    groupRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onClose: () => void;
    onSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    onDelete: () => Promise<void>;
}

export const GoalGroupEditorPanel = ({
    group,
    groupRules,
    moneyMap,
    onClose,
    onSave,
    onDelete,
}: GoalGroupEditorPanelProps) => {
    const toast = useToastController();
    const [groupName, setGroupName] = useState(group.name ?? '');
    const [groupDescription, setGroupDescription] = useState(
        group.description ?? '',
    );
    const [percentages, setPercentages] = useState<Record<string, string>>(() =>
        Object.fromEntries(
            groupRules.map((r) => [
                r.id ?? '',
                percentToInputString(r.percentage ?? 0),
            ]),
        ),
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const updatePercentage = (id: string, value: string) => {
        setPercentages((prev) => ({ ...prev, [id]: value }));
    };

    const totalPercentage = Object.values(percentages).reduce(
        (sum, s) => sum + parsePercentString(s),
        0,
    );
    const isPercentageValid = Math.abs(totalPercentage - 1) < 0.005;

    const handleSave = async () => {
        const updatedGroups = (moneyMap.moneyMapRuleGroups ?? []).map((g) => {
            if (g.id !== group.id) return g;
            return { ...g, name: groupName, description: groupDescription };
        });

        const updatedRules = (moneyMap.moneyMapRules ?? []).map((r) => {
            if (r.moneyMapRuleGroupId !== group.id) return r;
            const pctStr = percentages[r.id ?? ''];
            const newPercentage =
                pctStr != null ? parsePercentString(pctStr) : undefined;
            return newPercentage != null
                ? { ...r, percentage: newPercentage }
                : r;
        });

        setIsSaving(true);
        try {
            const success = await onSave({
                ...moneyMap,
                moneyMapRuleGroups: updatedGroups,
                moneyMapRules: updatedRules,
            });

            if (success) {
                toast.show('Goal set saved', MutationSuccessToastOptions);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <YStack
            flex={1}
            height="100%"
            backgroundColor="$white"
            overflow="hidden"
        >
            <XStack
                alignItems="center"
                justifyContent="space-between"
                paddingHorizontal={18}
                paddingTop={20}
                paddingBottom={16}
            >
                <H6Regular>Goal group details</H6Regular>
                <IconButton
                    variant="secondary"
                    size="tiny"
                    icon={XIcon}
                    onPress={onClose}
                />
            </XStack>

            <SmoothieScrollView
                containerStyle={{
                    marginHorizontal: 0,
                    $sm: { alignSelf: 'stretch' },
                    '$platform-web': { marginTop: 0, paddingBottom: 0 },
                }}
            >
                <YStack gap={12} paddingHorizontal={18} paddingBottom={80}>
                    <YStack gap={4} paddingTop={4}>
                        <StyledBodyMediumRegularInputNoOutline
                            value={groupName}
                            onChangeText={setGroupName}
                            textAlign="left"
                            fontSize={24}
                            paddingHorizontal={0}
                        />
                        <StyledBodyMediumRegularInputNoOutline
                            value={groupDescription}
                            onChangeText={setGroupDescription}
                            placeholder="Enter description"
                            textAlign="left"
                            color="$gray1000"
                            placeholderTextColor="$gray800"
                            paddingHorizontal={0}
                        />
                    </YStack>

                    <RowList rowHeaderProps={{ title: 'Percentage split' }}>
                        {groupRules.map((rule, index) => (
                            <PercentageSplitRow
                                key={rule.id ?? index}
                                label={rule.description ?? ''}
                                value={
                                    percentages[rule.id ?? ''] ??
                                    percentToInputString(rule.percentage ?? 0)
                                }
                                onChange={(v) => {
                                    updatePercentage(rule.id ?? '', v);
                                }}
                            />
                        ))}
                    </RowList>

                    {!isPercentageValid && (
                        <XStack
                            backgroundColor="$orange300"
                            borderRadius={12}
                            padding={12}
                            alignItems="center"
                        >
                            <LabelRegular color="$orange1100" flex={1}>
                                Percentages must add up to 100%
                            </LabelRegular>
                        </XStack>
                    )}
                </YStack>
            </SmoothieScrollView>

            <YStack
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                paddingHorizontal={18}
                paddingVertical={16}
                backgroundColor="$white"
            >
                <XStack alignItems="center" justifyContent="space-between">
                    <Button
                        variant="tertiary"
                        size="small"
                        tone="danger"
                        icon={isDeleting ? <SpinnerIcon /> : Trash2Icon}
                        iconPosition="left"
                        disabled={isDeleting || isSaving}
                        onPress={handleDelete}
                    >
                        {isDeleting ? 'Deleting' : 'Delete'}
                    </Button>
                    <Button
                        variant="primary"
                        size="small"
                        icon={isSaving ? <SpinnerIcon /> : undefined}
                        iconPosition="left"
                        disabled={isSaving || isDeleting || !isPercentageValid}
                        onPress={handleSave}
                    >
                        {isSaving ? 'Saving' : 'Save'}
                    </Button>
                </XStack>
            </YStack>
        </YStack>
    );
};
